// Lightweight persistence. We only cache the bits the server already returns
// publicly on login (KDF params + encrypted_vault_key) so unlock can re-derive
// without an extra round-trip. NOTHING sensitive is stored in plaintext.

interface PersistedState {
  kdfSaltB64?: string;
  kdfOpslimit?: number;
  kdfMemlimit?: number;
  encryptedVaultKey?: { ciphertextB64: string; nonceB64: string };
  refreshToken?: string;
  // Face ID / Touch ID — vault key wrapped with a PRF secret bound to the
  // platform authenticator. Cleared on master-password change or manual disable.
  faceIdCredentialIdB64?: string;
  faceIdSaltB64?: string;
  faceIdNonceB64?: string;
  faceIdCiphertextB64?: string;
  // One-time prompt control: user has been asked whether to enable Face ID.
  faceIdAsked?: boolean;
}

const KEY = "pm-webapp-state";

export function loadState(): PersistedState {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as PersistedState) : {};
  } catch {
    return {};
  }
}

export function saveState(patch: Partial<PersistedState>): void {
  const cur = loadState();
  localStorage.setItem(KEY, JSON.stringify({ ...cur, ...patch }));
}

export function clearState(): void {
  localStorage.removeItem(KEY);
}

export function clearFaceId(): void {
  const cur = loadState();
  delete cur.faceIdCredentialIdB64;
  delete cur.faceIdSaltB64;
  delete cur.faceIdNonceB64;
  delete cur.faceIdCiphertextB64;
  localStorage.setItem(KEY, JSON.stringify(cur));
}

export function getFaceIdBundle(): {
  credentialIdB64: string;
  saltB64: string;
  nonceB64: string;
  ciphertextB64: string;
} | null {
  const s = loadState();
  if (
    !s.faceIdCredentialIdB64 ||
    !s.faceIdSaltB64 ||
    !s.faceIdNonceB64 ||
    !s.faceIdCiphertextB64
  ) {
    return null;
  }
  return {
    credentialIdB64: s.faceIdCredentialIdB64,
    saltB64: s.faceIdSaltB64,
    nonceB64: s.faceIdNonceB64,
    ciphertextB64: s.faceIdCiphertextB64,
  };
}
